import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  ButtonProps,
  Collapse,
  CollapseProps,
  DialogActions,
  DialogContent,
  DialogContentProps,
  DialogProps,
  DialogTitle,
  Dialog as MUIDialog,
  Stack,
  SxProps,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import React from "react";

import { SaveButton, SaveButtonProps } from "./SaveButton";

export function SafeDialog(props: DialogProps) {
  return props.open ? (
    <MUIDialog {...props} open={true}>
      {props.children}
    </MUIDialog>
  ) : (
    <></>
  );
}
export type Props = Omit<DialogProps, "onClose" | "content" | "title"> & {
  /** Function called when the dialog is closed. Can be a simple callback or receive the close reason. */
  onClose?:
    | (() => void)
    | ((
        event: React.MouseEvent<HTMLElement>,
        reason: "backdropClick" | "escapeKeyDown" | "cancelButtonClick" | "closeButtonClick"
      ) => void);
  /** Title displayed at the top of the dialog */
  title?: React.ReactNode;
  /** Optional subheader displayed below the title */
  subheader?: string;
  /** Optional description displayed in the content area of the dialog */
  description?: React.ReactNode;
  /** Optional element to display in the title bar to the left of the close button */
  titleBarActionElement?: React.ReactNode;
  hideCloseButton?: boolean;
  /** Optional element to display in the close button */
  closeIcon?: React.ReactNode;
  /** Optional tooltip to display when hovering over the close button */
  closeIconTooltip?: string;
  /** Additional Primary Actions. These will display to the left of Cancel and Primary action button */
  additionalPrimaryActionElements?: React.ReactNode[];
  /** Secondary Actions will display on the bottom left of the action toolbar */
  secondaryActionElements?: React.ReactNode[];
  hideCancelButton?: boolean;
  cancelButtonProps?: ButtonProps & { text?: string };
  /** Whether to show a primary action (e.g., Save button). Defaults to true. */
  noPrimaryAction?: boolean;
  /** Optionally provide a component to use as the primary action button. `primaryActionButtonProps` do not apply to this */
  primaryActionButton?: React.ReactNode;
  /** Props to configure the Save button (shown if `noPrimaryAction` is false) */
  primaryActionButtonProps?: SaveButtonProps;
  /** Optional styles to apply to the content area of the dialog */
  contentSx?: SxProps;
  DialogContentProps?: DialogContentProps;
  /** Props to pass to the Collapse component that wraps the dialog content and actions */
  CollapseProps?: CollapseProps;
};

export function Dialog(props: Props) {
  const {
    title,
    subheader,
    description,
    titleBarActionElement,
    hideCloseButton,
    noPrimaryAction,
    hideCancelButton,
    primaryActionButtonProps,
    primaryActionButton,
    additionalPrimaryActionElements,
    secondaryActionElements,
    onClose,
    closeIcon,
    closeIconTooltip = "Close",
    children,
    cancelButtonProps,
    contentSx,
    DialogContentProps,
    CollapseProps,
    ...dialogProps
  } = props;
  const theme = useTheme();

  const noActions =
    !secondaryActionElements?.length && !additionalPrimaryActionElements?.length && noPrimaryAction;

  return (
    <SafeDialog scroll="paper" {...dialogProps} onClose={onClose}>
      <DialogTitle
        justifyContent={title ? "space-between" : "flex-end"}
        display="flex"
        alignItems="center"
      >
        {title}
        <Box display="flex" alignItems="center" gap={1}>
          {titleBarActionElement}
          {!hideCloseButton && onClose && (
            <Tooltip title={closeIconTooltip}>
              <IconButton aria-label="close" onClick={(e) => onClose(e, "closeButtonClick")}>
                {closeIcon ?? <CloseIcon />}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </DialogTitle>
      <DialogContent
        {...DialogContentProps}
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          ...DialogContentProps?.sx,
        }}
      >
        <Collapse
          in={CollapseProps?.in ?? true}
          timeout={CollapseProps?.timeout ?? 700}
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            "& .MuiCollapse-root, & .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner": {
              flex: 1,
              display: "flex",
              flexDirection: "column",
            },
            ...CollapseProps?.sx,
          }}
          {...CollapseProps}
        >
          <Stack
            spacing={2}
            pt={theme.spacing(1.5)}
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              ...contentSx,
            }}
          >
            {subheader && (
              <Typography fontWeight={500} variant="subtitle1">
                {subheader}
              </Typography>
            )}
            {description && (
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {description}
              </Typography>
            )}
            {children}
          </Stack>
        </Collapse>
      </DialogContent>

      {!noActions && CollapseProps?.in !== false && (
        <DialogActions>
          <Collapse
            in={CollapseProps?.in ?? true}
            timeout={CollapseProps?.timeout ?? 700}
            sx={
              CollapseProps?.sx ?? {
                width: "100%",
                ".MuiCollapse-root .MuiCollapse-wrapper": {
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                },
              }
            }
            {...CollapseProps}
          >
            <Stack direction="row" justifyContent="space-between" gap={theme.spacing(2)} flex={1}>
              {...secondaryActionElements ?? []}
              <Box
                sx={{
                  display: "flex",
                  gap: theme.spacing(2),
                  justifyContent: "flex-end",
                  alignItems: "center",
                  flex: 1,
                }}
              >
                {...additionalPrimaryActionElements ?? []}
                {!noPrimaryAction && !hideCancelButton && onClose && (
                  <Button
                    onClick={(e) => onClose(e, "cancelButtonClick")}
                    disabled={primaryActionButtonProps?.loading}
                    {...cancelButtonProps}
                  >
                    {cancelButtonProps?.text ?? "Cancel"}
                  </Button>
                )}
                {!noPrimaryAction &&
                  (primaryActionButton ?? <SaveButton {...primaryActionButtonProps} />)}
              </Box>
            </Stack>
          </Collapse>
        </DialogActions>
      )}
    </SafeDialog>
  );
}
